const GEMINI_MODEL = 'gemini-2.5-flash';

/**
 * Generates a full SEO-optimized article using Gemini AI with custom styling and images.
 * @param {string} apiKey - The user's Gemini API key from the UI.
 * @param {string} title - The article title.
 * @param {string} description - A brief description of what the article should cover.
 * @param {string} keywords - Comma-separated target SEO keywords.
 * @param {string} fontStyle - 'auto' or a specific font family name.
 * @param {string} colorStyle - 'auto' or a specific hex color code.
 * @param {string} images - Newline or comma-separated list of image URLs.
 * @returns {Promise<{content: string, seoTitle: string, seoDescription: string, slug: string}>}
 */
export const generateArticle = async (apiKey, title, description, keywords, fontStyle = 'auto', colorStyle = 'auto', images = '') => {
  if (!apiKey || apiKey.trim() === '') {
    throw new Error('الرجاء إدخال مفتاح Gemini API الخاص بك.');
  }

  const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${apiKey}`;

  const currentDate = new Date().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });

  const prompt = `أنت باحث مالي محترف، كاتب محتوى مبسط، وخبير سيو (SEO Expert). 

المهمة: قم بالبحث في شبكة الإنترنت (عبر أداة بحث جوجل المتاحة لك) عن المقالات الحالية المتعلقة بالعنوان والفكرة أدناه. اقرأ أهم المقالات وقم بتجميع وتلخيص أفضل المعلومات الدقيقة والمحدثة. ثم، اكتب مقالاً حصرياً، مبسطاً جداً يمكن لأي شخص عادي فهمه (بأسلوب السهل الممتنع)، ويكون متصدراً لنتائج جوجل (Rank #1).

المعلومات الأساسية:
- تاريخ اليوم: ${currentDate}
- العنوان المطلوب البحث عنه والكتابة حوله: "${title}"
- الفكرة: "${description}"
- الكلمات المفتاحية: "${keywords}"

تعليمات التنسيق:
- الخط: ${fontStyle === 'auto' ? 'اختر خطاً مناسباً' : `"${fontStyle}"`}
- اللون: ${colorStyle === 'auto' ? 'اختر لوناً' : `"${colorStyle}"`}
- روابط الصور: ${images || 'لا يوجد'}

كيف تكتب كباحث مبسط (Research & Simplify):
1. **الأسلوب (السهل الممتنع):** بعد جمع المعلومات من بحثك، قم بصياغتها كأنك تشرحها لشخص مبتدئ تماماً في السوق. استخدم لغة بسيطة، مباشرة، خالية من التعقيد المبالغ فيه.
2. **التباين الفوضوي (High Burstiness):** تلاعب بأطوال الجمل. اكتب جملة قصيرة جداً للتأكيد. ثم فقرة تشرح ببساطة. (مثال: الأمر بسيط جداً. عندما ترتفع الفائدة، يهرب المستثمرون إلى الدولار ويتركون الذهب، وهذا يعني...).
3. **العمق المبسط:** لا تستخدم مصطلحات معقدة دون شرحها بين قوسين أو في جملة بسيطة.
4. **تجنب النمطية:** لا تستخدم (علاوة على ذلك، في الختام، مما لا شك فيه). استخدم (الخلاصة، باختصار، اللي يهمك كمبتدئ).

قواعد السيو الصارمة جداً (SEO Checklist - يجب تنفيذها بنسبة 100%):
1. **الكلمة المفتاحية الرئيسية (Focus Keyword):**
   - يجب كتابتها بالنص (كما هي) في أول 50 كلمة من المقال.
   - يجب كتابتها في الفقرة الأخيرة (الخاتمة) بالنص.
   - كررها بشكل طبيعي داخل النص من 4 إلى 6 مرات كحد أقصى لتكون الكثافة مثالية (حوالي 1.5%).
2. **العناوين (Headings):**
   - يجب استخدام <h2> و <h3> لتقسيم المقال بوضوح (لا تستخدم <h1>).
   - يجب أن يحتوي عنوان <h2> واحد على الأقل على الكلمة المفتاحية الرئيسية بالنص.
3. **التنسيق وبناء المحتوى:**
   - يجب أن لا يقل طول المقال عن 800 كلمة مفيدة ومستخلصة من أحدث المقالات التي بحثت عنها.
   - ضع الكلمات المفتاحية الرئيسية والفرعية بين علامات التغميق <strong>.
   - استخدم القوائم النقطية <ul> أو الرقمية <ol> لتبسيط وتلخيص النقاط المهمة.
   - **إخلاء مسؤولية (مهم جداً):** في نهاية المقال تماماً (بعد الخاتمة)، أضف فقرة إخلاء مسؤولية قانونية تنص بوضوح على أن (الآراء الواردة في هذا المقال هي للأغراض المعلوماتية والتعليمية فقط، ولا تعتبر نصيحة مالية أو توصية بشراء أو بيع الذهب، وأن الكاتب والموقع غير مسؤولين عن أي قرارات استثمارية يتخذها القارئ). ضع هذه الفقرة داخل وسم <div style="background-color: #f3f4f6; padding: 15px; border-radius: 8px; font-size: 0.9em; color: #4b5563; margin-top: 30px; border-right: 4px solid #ef4444;">...</div>.
4. **إدراج الصور:**
   - ضع روابط الصور المتاحة باستخدام الكود التالي: <img src="..." alt="[يجب وضع الكلمة المفتاحية الرئيسية في الـ alt]" style="...">.
5. **تنسيق التصميم الداخلي (Inline CSS):**
   - يجب تطبيق style="..." على العناوين (H2, H3) باللون والخط المحددين لتظهر بشكل جميل.

الناتج المطلوب: كود HTML نقي (لا تستخدم markdown أو \`\`\`html أبداً). لا تكتب أي نص قبل أو بعد كود الـ HTML.

في النهاية، بعد كود الـ HTML مباشرة، اكتب المعلومات التالية بالتنسيق التالي بدقة شديدة:
---SEO_DATA---
SEO_TITLE: [عنوان جذاب يحتوي الكلمة المفتاحية الرئيسية، طوله بين 50 و 60 حرفاً فقط]
SEO_DESC: [وصف Meta Description تسويقي يحتوي الكلمة المفتاحية، طوله بين 120 و 155 حرفاً فقط]
SEO_SLUG: [الكلمة-المفتاحية-مفصولة-بشرطات]`;

  const response = await fetch(GEMINI_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: [
        {
          parts: [{ text: prompt }],
        },
      ],
      tools: [
        {
          googleSearch: {}
        }
      ],
      generationConfig: {
        temperature: 0.85,
        maxOutputTokens: 8192,
      },
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || `خطأ في الاتصال بـ Gemini API (${response.status})`);
  }

  const data = await response.json();
  const rawText = data?.candidates?.[0]?.content?.parts?.[0]?.text;

  if (!rawText) {
    throw new Error('لم يتم استلام رد من Gemini. حاول مرة أخرى.');
  }

  // Parse the response to separate article content from SEO data
  return parseGeminiResponse(rawText);
};

/**
 * Parses the raw Gemini response into structured data.
 */
function parseGeminiResponse(raw) {
  let content = raw;
  let seoTitle = '';
  let seoDescription = '';
  let slug = '';

  // Check if SEO data section exists
  const seoMarker = '---SEO_DATA---';
  const seoIndex = raw.indexOf(seoMarker);

  if (seoIndex !== -1) {
    content = raw.substring(0, seoIndex).trim();
    const seoSection = raw.substring(seoIndex + seoMarker.length).trim();

    const titleMatch = seoSection.match(/SEO_TITLE:\s*(.+)/);
    const descMatch = seoSection.match(/SEO_DESC:\s*(.+)/);
    const slugMatch = seoSection.match(/SEO_SLUG:\s*(.+)/);

    if (titleMatch) seoTitle = titleMatch[1].trim();
    if (descMatch) seoDescription = descMatch[1].trim();
    if (slugMatch) slug = slugMatch[1].trim();
  }

  // Clean up any markdown code fences that Gemini might add
  content = content.replace(/```html\n?/gi, '').replace(/```\n?/g, '').trim();

  // Append a Call-To-Action (CTA) link back to the homepage
  const ctaBlock = `
<div class="article-cta" style="margin-top: 30px; padding: 20px; background-color: var(--color-gold-bg, #FDF7EA); border-right: 4px solid var(--color-gold, #C5A059); border-radius: 8px;">
  <p style="margin: 0; font-size: 1.1rem; font-weight: bold; color: var(--color-text-primary);">
    لمتابعة الأسعار وتحديثات السوق لحظة بلحظة، يمكنك زيارة <a href="/" style="color: var(--color-gold-dark, #A68241); text-decoration: underline;">الصفحة الرئيسية لأسعار الذهب</a>.
  </p>
</div>
`;
  content = content + ctaBlock;

  return { content, seoTitle, seoDescription, slug };
}
